import React, { useEffect, useState } from 'react';
import { 
  Table, 
  App,
  Button, 
  Space, 
  Row, 
  Col, 
  Select, 
  DatePicker,
  Tag,
  Tooltip,
  Input
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  PrinterOutlined,
  EditOutlined,
  PlusOutlined,
  BankOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { PageTitle, StyledCard, SearchableSelect } from '../common';
import GrabarAsientoModal from './GrabarAsientoModal';
import { theme } from '../../theme';

const { Option } = Select;
const { RangePicker } = DatePicker;

// Interfaces
interface ComprasAlgodon {
  id: number;
  nro_compra: number;
  comprobante: string;
  fecha: string;
  cliente: number;
  cliente_nombre: string;
  cliente_nc: string;
  subtotal: number;
  iva: number;
  impuesto_interno: number;
  retenciones: number;
  total_kilos: number;
  alta: number;
  empresa: number;
  empresa_nombre: string;
  sucursal: number;
  sucursal_nombre: string;
  asiento_contable_id: number | null;
  tipo_comprobante_contable_id: number | null;
}

interface Cliente {
  id: number;
  denominacion: string;
  nc: string;
}

const ListarCompraAlgodon: React.FC = () => {
  const { message, modal } = App.useApp();
  const [compras, setCompras] = useState<ComprasAlgodon[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [loadingClientes, setLoadingClientes] = useState(false);
  const { getAccessTokenSilently, user } = useAuth0();
  const navigate = useNavigate();
  const [tieneContable, setTieneContable] = useState(false);
  const [modalAsiento, setModalAsiento] = useState<{ open: boolean; compra: ComprasAlgodon | null }>({ open: false, compra: null });
  
  // Estados para búsqueda de cliente
  const [searchCliente, setSearchCliente] = useState('');
  const [showSearchCliente, setShowSearchCliente] = useState(false);
  const [selectedCliente, setSelectedCliente] = useState<Cliente | null>(null);
  
  // Estados para filtros
  const [filtros, setFiltros] = useState({
    fechaDesde: dayjs().subtract(30, 'day') as dayjs.Dayjs | null,
    fechaHasta: dayjs() as dayjs.Dayjs | null,
    cliente: undefined as number | undefined,
    estado: '' as string,
  });

  // Estados para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  // Cargar clientes al inicializar y ejecutar búsqueda con filtros por defecto
  useEffect(() => {
    if (user?.id_empresa) {
      fetchClientes();
      fetchCompras();
      getAccessTokenSilently().then(token => {
        axios.get(
          `${process.env.REACT_APP_LIQUIDACION_URL}/verificar_empresa_contable/${user.id_empresa}/`,
          { headers: { Authorization: `Bearer ${token}` } }
        ).then(res => setTieneContable(res.data.tiene_contable === true)).catch(() => {});
      }).catch(() => {});
    }
  }, [user]);

  // Cargar compras solo cuando cambie la página o el tamaño de página
  useEffect(() => {
    if (user?.id_empresa && (currentPage > 1 || pageSize !== 10)) {
      fetchCompras();
    }
  }, [user, currentPage, pageSize]);

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.cliente-search-container')) {
        setShowSearchCliente(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Función para obtener los clientes
  const fetchClientes = async () => {
    try {
      setLoadingClientes(true);
      
      if (!user?.id_empresa) {
        console.warn('id_empresa no disponible');
        return;
      }

      if (!process.env.REACT_APP_PADRON_URL) {
        console.error('REACT_APP_PADRON_URL no está configurada');
        message.error('Error de configuración: URL del padrón no disponible');
        return;
      }

      const token = await getAccessTokenSilently();
      const response = await axios.get(
        `${process.env.REACT_APP_PADRON_URL}/leer_padron/${user.id_empresa}/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (Array.isArray(response.data)) {
        setClientes(response.data);
      } else if (response.data.success && response.data.data) {
        setClientes(response.data.data);
      }
    } catch (error) {
      console.error('Error al cargar clientes:', error);
      message.error('Error al cargar la lista de clientes');
    } finally {
      setLoadingClientes(false);
    }
  };

  // Función para obtener las compras
  const fetchCompras = async () => {
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      
      if (!user?.id_empresa) {
        message.error('Error: Datos de empresa no disponibles');
        return;
      }
      
      const params = new URLSearchParams();
      
      if (filtros.fechaDesde) {
        params.append('fecha_desde', filtros.fechaDesde.format('YYYY-MM-DD'));
      }
      if (filtros.fechaHasta) {
        params.append('fecha_hasta', filtros.fechaHasta.format('YYYY-MM-DD'));
      }
      
      params.append('page', currentPage.toString());
      params.append('page_size', pageSize.toString());

      if (filtros.estado) {
        params.append('estado', filtros.estado);
      }

      // Construir la URL con id_empresa en la ruta
      // Si hay cliente, va como parte de la ruta, no como query param
      const queryString = params.toString();
      let url = `${process.env.REACT_APP_LIQUIDACION_URL}/listar_compras_algodon/${user.id_empresa}`;
      if (filtros.cliente) {
        url += `/${filtros.cliente}`;
      }
      url += '/';
      if (queryString) {
        url += `?${queryString}`;
      }

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // El backend devuelve: { results, count, page, page_size, total_pages, ... }
      if (response.data && response.data.results) {
        setCompras(response.data.results);
        setTotal(response.data.count || response.data.results.length);
        setHasSearched(true);
      } else {
        message.error('Error al cargar las compras');
      }
    } catch (error) {
      console.error('Error al cargar compras:', error);
      message.error('Error al cargar las compras de algodón');
    } finally {
      setLoading(false);
    }
  };

  // Función para manejar la búsqueda
  const handleBuscar = () => {
    setCurrentPage(1);
    fetchCompras();
  };

  // Función para limpiar filtros
  const handleLimpiarFiltros = () => {
    setFiltros({
      fechaDesde: dayjs().subtract(30, 'day'),
      fechaHasta: dayjs(),
      cliente: undefined,
      estado: '',
    });
    setSelectedCliente(null);
    setSearchCliente('');
    setCurrentPage(1);
    setHasSearched(false);
    setCompras([]);
    setTotal(0);
  };

  // Función para manejar la selección de cliente
  const handleClienteSelect = (cliente: Cliente) => {
    setSelectedCliente(cliente);
    setFiltros({ ...filtros, cliente: cliente.id });
    setShowSearchCliente(false);
  };

  // Obtener el tag para el estado
  const getEstadoTag = (alta: number) => {
    switch (alta) {
      case 1:
        return <Tag color="green">Activo</Tag>;
      case 0:
        return <Tag color="red">Inactivo</Tag>;
      case 2:
        return <Tag color="blue">Emitido</Tag>;
      default:
        return <Tag color="default">Desconocido</Tag>;
    }
  };

  // Formatear montos con separador de miles
  const formatearMonto = (value: number | string) => {
    if (value === null || value === undefined) {
      return '0';
    }
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    return new Intl.NumberFormat('es-AR').format(numValue);
  };

  const columns: ColumnsType<ComprasAlgodon> = [
    {
      title: 'N° Compra',
      dataIndex: 'nro_compra',
      key: 'nro_compra',
      width: 100,
      sorter: (a, b) => a.nro_compra - b.nro_compra,
    },
    {
      title: 'Comprobante',
      dataIndex: 'comprobante',
      key: 'comprobante',
      width: 150,
    },
    {
      title: 'Fecha',
      dataIndex: 'fecha',
      key: 'fecha',
      width: 100,
      render: (value) => dayjs(value).format('DD/MM/YYYY'),
      sorter: (a, b) => dayjs(a.fecha).unix() - dayjs(b.fecha).unix(),
    },
    {
      title: 'Cliente',
      dataIndex: 'cliente_nombre',
      key: 'cliente_nombre',
      width: 200,
      ellipsis: true,
    },
    {
      title: 'N° Cliente',
      dataIndex: 'cliente_nc',
      key: 'cliente_nc',
      width: 100,
    },
    {
      title: 'Subtotal',
      dataIndex: 'subtotal',
      key: 'subtotal',
      width: 120,
      align: 'right',
      render: (value) => `$ ${formatearMonto(value)}`,
    },
    {
      title: 'IVA',
      dataIndex: 'iva',
      key: 'iva',
      width: 100,
      align: 'right',
      render: (value) => `$ ${formatearMonto(value)}`,
    },
    {
      title: 'Imp. Interno',
      dataIndex: 'impuesto_interno',
      key: 'impuesto_interno',
      width: 120,
      align: 'right',
      render: (value) => `$ ${formatearMonto(value)}`,
    },
    {
      title: 'Retenciones',
      dataIndex: 'retenciones',
      key: 'retenciones',
      width: 120,
      align: 'right',
      render: (value) => `$ ${formatearMonto(value)}`,
    },
    {
      title: 'Total Kilos',
      dataIndex: 'total_kilos',
      key: 'total_kilos',
      width: 120,
      align: 'right',
      render: (value) => formatearMonto(value),
    },
    {
      title: 'Estado',
      dataIndex: 'alta',
      key: 'alta',
      width: 80,
      align: 'center',
      render: (value) => getEstadoTag(value),
      filters: [
        { text: 'Activo', value: 1 },
        { text: 'Inactivo', value: 0 },
      ],
      onFilter: (value, record) => record.alta === value,
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 200,
      fixed: 'right' as const,
      render: (_, record) => (
        <Space size="small">
          {user?.tipo_usuario === 'D' && record.alta !== 0 && (
            <Tooltip title={record.asiento_contable_id != null ? 'No editable: asiento registrado' : 'Editar'}>
              <Button
                type="primary"
                icon={<EditOutlined />}
                size="small"
                disabled={record.asiento_contable_id != null}
                onClick={() => navigate(`/editar_compraalgodon/${record.id}`)}
              />
            </Tooltip>
          )}
          <Tooltip title="Imprimir">
            <Button
              icon={<PrinterOutlined />}
              size="small"
              onClick={() => navigate(`/imprimir_compraalgodon/${record.id}`)}
            />
          </Tooltip>
          <Tooltip title="Emitir">
            <Button
              icon={<SendOutlined />}
              size="small"
              style={{ backgroundColor: theme.colors.secondary, borderColor: theme.colors.secondary, color: 'white' }}
              onClick={() => navigate(`/liquidacion/emision_compraalgodon/${record.id}`)}
            />
          </Tooltip>
          {(() => {
            const cpte = record.comprobante || '';
            const sinAfip = cpte.startsWith('0000');
            const asientoHecho = record.asiento_contable_id != null;
            const asientoHabilitado = !asientoHecho && !sinAfip && record.alta !== 0;

            return (
              <>
                {tieneContable && (
                  <Tooltip title={
                    record.alta === 0 ? 'No disponible: liquidación anulada' :
                    asientoHabilitado ? 'Grabar Asiento Contable' :
                    `Asiento N° ${record.asiento_contable_id} — Cpte: ${cpte}`
                  }>
                    <Button
                      icon={<BankOutlined />}
                      size="small"
                      style={
                        asientoHabilitado
                          ? { backgroundColor: '#6366f1', borderColor: '#6366f1', color: 'white' }
                          : { backgroundColor: theme.colors.success, borderColor: theme.colors.success, color: 'white' }
                      }
                      disabled={!asientoHabilitado}
                      onClick={asientoHabilitado ? () => setModalAsiento({ open: true, compra: record }) : undefined}
                    />
                  </Tooltip>
                )}
              </>
            );
          })()}
        </Space>
      ),
    },
  ];


  const handleTableChange = (pagination: any) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const handleNuevaCompra = () => {
    navigate('/liquidacion/nueva_compraalgodon');
  };

  const handleAsientoSuccess = (numeroAsiento: number, nroComprobante: string) => {
    const idGrabado = modalAsiento.compra?.id;
    setModalAsiento({ open: false, compra: null });
    message.success(`Asiento N° ${numeroAsiento} grabado — Cpte: ${nroComprobante}`);
    setCompras(prev => prev.map(c =>
      c.id === idGrabado
        ? { ...c, asiento_contable_id: numeroAsiento, comprobante: nroComprobante }
        : c
    ));
  };

  return (
    <div style={{ padding: '0' }}>
      {modalAsiento.compra && (
        <GrabarAsientoModal
          open={modalAsiento.open}
          onClose={() => setModalAsiento({ open: false, compra: null })}
          onSuccess={handleAsientoSuccess}
          tipo="compra"
          idEmpresa={modalAsiento.compra.empresa}
          idSucursal={modalAsiento.compra.sucursal}
          idLiquidacion={modalAsiento.compra.id}
          nroInterno={modalAsiento.compra.nro_compra}
          clienteNombre={modalAsiento.compra.cliente_nombre}
          subtotal={modalAsiento.compra.subtotal}
          iva={modalAsiento.compra.iva}
          retenciones={modalAsiento.compra.retenciones}
        />
      )}
      {/* Título de la página */}
      <h1 style={{ 
        color: theme.colors.primaryDark, 
        fontSize: '28px', 
        fontWeight: 600, 
        marginBottom: '24px' 
      }}>
        Liquidación Compra Algodón
      </h1>

      <StyledCard 
        title="Filtros de Búsqueda"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleNuevaCompra}
            size="large"
            style={{
              backgroundColor: theme.colors.secondary,
              borderColor: theme.colors.secondary,
              fontWeight: 500,
              minWidth: '140px'
            }}
          >
            Nueva Compra
          </Button>
        }
      >
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(5, 1fr)',
            gap: '20px',
            alignItems: 'end'
          }}>
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: 600,
                color: theme.colors.textPrimary,
                fontSize: '14px'
              }}>
                Fecha (Desde - Hasta):
              </label>
              <RangePicker
                format="DD/MM/YYYY"
                value={[filtros.fechaDesde, filtros.fechaHasta]}
                onChange={(dates) => {
                  setFiltros({
                    ...filtros,
                    fechaDesde: dates?.[0] || null,
                    fechaHasta: dates?.[1] || null,
                  });
                }}
                size="large"
                style={{ width: '100%' }}
              />
            </div>

            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: 600,
                color: theme.colors.textPrimary,
                fontSize: '14px'
              }}>
                Cliente:
              </label>
              <div style={{ position: 'relative' }} className="cliente-search-container">
                <Input
                  placeholder="Buscar cliente..."
                  size="large"
                  prefix={
                    <SearchOutlined style={{ color: '#bfbfbf' }} />
                  }
                  value={selectedCliente ? `${selectedCliente.nc} - ${selectedCliente.denominacion}` : searchCliente}
                  onChange={(e) => {
                    setSearchCliente(e.target.value);
                    setSelectedCliente(null);
                    setShowSearchCliente(true);
                  }}
                  onClick={() => setShowSearchCliente(true)}
                  allowClear
                  onClear={() => {
                    setSearchCliente('');
                    setSelectedCliente(null);
                    setFiltros({ ...filtros, cliente: undefined });
                  }}
                  style={{ width: '100%' }}
                />
                {showSearchCliente && searchCliente && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: 'white',
                      border: '1px solid #d9d9d9',
                      borderRadius: '6px',
                      marginTop: '4px',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      zIndex: 1000,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                    }}
                  >
                    {clientes
                      .filter((c) =>
                        c.nc.toLowerCase().includes(searchCliente.toLowerCase()) ||
                        c.denominacion.toLowerCase().includes(searchCliente.toLowerCase())
                      )
                      .map((cliente) => (
                      <div
                        key={cliente.id}
                        style={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #f0f0f0'
                        }}
                        onMouseOver={e => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                        onMouseOut={e => (e.currentTarget.style.backgroundColor = 'white')}
                        onClick={() => handleClienteSelect(cliente)}
                      >
                        {cliente.nc} - {cliente.denominacion}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: 600,
                color: theme.colors.textPrimary,
                fontSize: '14px'
              }}>
                Estado:
              </label>
              <Select
                value={filtros.estado || undefined}
                placeholder="Todos"
                allowClear
                size="large"
                style={{ width: '100%' }}
                onChange={(value) => setFiltros({ ...filtros, estado: value ?? '' })}
              >
                <Option value="pendiente_asiento">Pendiente de asiento</Option>
                <Option value="afip_pendiente_asiento">Con AFIP, pendiente de asiento</Option>
                <Option value="asentado">Asentado en diario</Option>
                <Option value="anulado">Anulado</Option>
              </Select>
            </div>

            <div>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleBuscar}
                loading={loading}
                size="large"
                style={{
                  backgroundColor: theme.colors.secondary,
                  borderColor: theme.colors.secondary,
                  fontWeight: 500,
                  width: '100%'
                }}
              >
                Buscar
              </Button>
            </div>
            <div>
              <Button
                icon={<ReloadOutlined />}
                onClick={handleLimpiarFiltros}
                size="large"
                style={{
                  fontWeight: 500,
                  width: '100%'
                }}
              >
                Limpiar
              </Button>
            </div>
          </div>
        </StyledCard>

        {/* Tabla */}
        {!hasSearched ? (
          <StyledCard>
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: theme.colors.textMuted
            }}>
              <div style={{ fontSize: '16px', marginBottom: '8px' }}>
                🔍 Configure los filtros y haga clic en "Buscar" para ver las compras de algodón
              </div>
              <div style={{ fontSize: '14px' }}>
                Las fechas están predeterminadas para los últimos 30 días
              </div>
            </div>
          </StyledCard>
        ) : (
          <StyledCard title="Compras de Algodón" bodyStyle={{ padding: 0 }}>
            <Table
              columns={columns}
              dataSource={compras}
              rowKey="id"
              loading={loading}
              pagination={{
                current: currentPage,
                pageSize,
                total,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} de ${total} compras`,
                pageSizeOptions: ['10', '20', '50', '100'],
              }}
              onChange={handleTableChange}
              scroll={{ x: 1200 }}
              size="middle"
              locale={{
                emptyText: 'No se encontraron compras con los filtros seleccionados'
              }}
            />
          </StyledCard>
        )}
    </div>
  );
};

export default ListarCompraAlgodon;

